import { ShieldCheck, ShieldOff } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';

const TwoFactorSetup = () => {
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [step, setStep] = useState('idle'); // idle | setup | verify | disable
  const [qr, setQr] = useState(null);
  const [secret, setSecret] = useState(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/2fa/status');
      if (data.success) setMfaEnabled(data.mfaEnabled);
    } catch {}
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const startSetup = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/auth/2fa/setup');
      if (data.success) {
        setQr(data.qr);
        setSecret(data.secret);
        setStep('setup');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start 2FA setup');
    } finally {
      setLoading(false);
    }
  };

  const verifySetup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/auth/2fa/verify', { token: code });
      if (data.success) {
        setMfaEnabled(true);
        setStep('idle');
        setSuccess('Two-factor authentication is now enabled.');
        setCode('');
        setQr(null);
        setSecret(null);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const disable2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/auth/2fa/disable', { totp: code });
      if (data.success) {
        setMfaEnabled(false);
        setStep('idle');
        setSuccess('Two-factor authentication has been disabled.');
        setCode('');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
          {mfaEnabled
            ? <ShieldCheck size={20} className="text-emerald-400" />
            : <ShieldOff size={20} className="text-slate-400" />}
        </div>
        <div>
          <h3 className="text-slate-200 font-semibold">Two-Factor Authentication</h3>
          <p className="text-xs text-slate-400">
            {mfaEnabled ? 'Your account is protected with TOTP 2FA' : 'Add an extra layer of security'}
          </p>
        </div>
        <span className={`ml-auto text-xs px-2 py-0.5 rounded font-medium ${
          mfaEnabled ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 text-slate-400'
        }`}>
          {mfaEnabled ? 'Enabled' : 'Disabled'}
        </span>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-emerald-400 text-sm">
          {success}
        </div>
      )}

      {step === 'idle' && (
        <button
          onClick={mfaEnabled ? () => { setStep('disable'); setCode(''); setError(''); } : startSetup}
          disabled={loading}
          className={`w-full py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
            mfaEnabled
              ? 'bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30'
              : 'bg-emerald-600 hover:bg-emerald-500 text-white'
          }`}
        >
          {loading ? 'Loading...' : mfaEnabled ? 'Disable 2FA' : 'Enable 2FA'}
        </button>
      )}

      {step === 'setup' && qr && (
        <div className="space-y-4">
          <p className="text-sm text-slate-300">
            Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
          </p>
          <div className="flex justify-center">
            <img src={qr} alt="2FA QR Code" className="rounded-lg" width={180} height={180} />
          </div>
          <div className="bg-slate-700/50 rounded-lg p-3">
            <p className="text-xs text-slate-400 mb-1">Or enter this code manually:</p>
            <code className="text-xs text-emerald-400 font-mono break-all">{secret}</code>
          </div>
          <form onSubmit={verifySetup} className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">
                Enter the 6-digit code to confirm
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="w-full bg-slate-700 border border-slate-600 text-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500 tracking-widest text-center"
                autoFocus
                required
              />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => { setStep('idle'); setQr(null); setSecret(null); setCode(''); setError(''); }}
                className="flex-1 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg text-sm transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
              >
                {loading ? 'Verifying...' : 'Confirm & Enable'}
              </button>
            </div>
          </form>
        </div>
      )}

      {step === 'disable' && (
        <form onSubmit={disable2FA} className="space-y-3">
          <p className="text-sm text-slate-400">
            Enter your current 6-digit authentication code to confirm disabling 2FA.
          </p>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            value={code}
            onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            className="w-full bg-slate-700 border border-slate-600 text-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-500 tracking-widest text-center"
            autoFocus
            required
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { setStep('idle'); setCode(''); setError(''); }}
              className="flex-1 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg text-sm transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || code.length !== 6}
              className="flex-1 py-2 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
            >
              {loading ? 'Disabling...' : 'Disable 2FA'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default TwoFactorSetup;
